import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, ChevronDown, Mail, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../features/auth/data/context/AuthContext';
import { ThemeSwitcher } from '../ThemeSwitcher/ThemeSwitcher';
import './styles/LayoutStyles.css';

interface TopbarProps {
  userName: string;
  userEmail?: string;
}

const Topbar: React.FC<TopbarProps> = ({ userName, userEmail = '' }) => {
  const navigate = useNavigate();
  const { logout, state } = useAuthContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  const initials = userName
    ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'A';

  const logoSrc = state.user?.logo_url || "/logo.png";

  return (
    <header className="app-topbar">
      <div className="topbar-left">
      </div>

      <div className="topbar-center">
        <div className="topbar-brand-wrapper" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
          {state.user?.logo_url ? (
            <img src={state.user.logo_url} alt="Company Logo" className="topbar-center-logo" style={{ height: '40px', objectFit: 'contain' }} />
          ) : (
            <img src="/logo_name.png" alt="Brand Logo" className="topbar-center-logo" style={{ height: '50px', objectFit: 'contain' }} />
          )}
        </div>
      </div>

      <div className="topbar-right">

        <div className="topbar-actions">


          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '12px', marginRight: '12px' }}>
            <ThemeSwitcher />
          </div>

          <a
            href="https://www.jozuna.com/contact_us.html"
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-action-btn"
            title="Contact Us"
          >
            <Mail size={20} />
          </a>

          {/* 
          <button className="topbar-action-btn" title="Help">
            <HelpCircle size={20} />
          </button>
          */}

          <div ref={dropdownRef} className="topbar-profile-wrapper">
            <div
              className={`user-profile-trigger ${isDropdownOpen ? 'active' : ''}`}
              onClick={() => setIsDropdownOpen((v) => !v)}
            >
              <div className="user-avatar">
                <span className="user-avatar-initials">{initials}</span>
              </div>
              <span className="user-name">{userName}</span>
              <ChevronDown size={14} className={`chevron-icon ${isDropdownOpen ? 'rotated' : ''}`} />
            </div>

            {isDropdownOpen && (
              <div className="user-profile-dropdown">
                <div className="dropdown-header">
                  <div className="dropdown-avatar">
                    <span>{initials}</span>
                  </div>
                  <div className="dropdown-user-info">
                    <span className="dropdown-user-name">{userName}</span>
                    {userEmail && <span className="dropdown-user-email">{userEmail}</span>}
                  </div>
                </div>

                <div className="dropdown-divider" />

                <div className="dropdown-category">Manage</div>

                <div className="dropdown-item" onClick={() => { setIsDropdownOpen(false); navigate('/account/info'); }}>
                  <User size={15} />
                  <span>Account Management</span>
                </div>

                {state.user?.role_name === 'Super Admin' && (
                  <div className="dropdown-item" onClick={() => { setIsDropdownOpen(false); navigate('/settings/whitelabel'); }}>
                    <Settings size={15} />
                    <span>Settings</span>
                  </div>
                )}

                <div className="dropdown-divider" />

                <div className="dropdown-item dropdown-item-logout" onClick={handleLogout}>
                  <LogOut size={15} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
